import 'photoswipe/dist/photoswipe.css'
import { AnimatePresence, motion } from 'motion/react'
import type {
  DetailedHTMLProps,
  HTMLAttributes,
  ImgHTMLAttributes,
  MouseEvent,
  ReactNode,
} from 'react'
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Gallery, Item } from 'react-photoswipe-gallery'
import { tv } from 'tailwind-variants'
import { match } from 'ts-pattern'
import type { JsonValue } from 'type-fest'
import * as v from 'valibot'
import IcBaselineClose from '~icons/ic/baseline-close'
import { MediaStreamPlayer } from 'src/components/Media/MediaStreamPlayer'
import { Preloader } from 'src/components/Preloader/Preloader'
import Text from 'src/components/ui/Text'
import { useLocalizedStringPairGetter } from 'src/i18n/localizedContent'
import type {
  CampaignMediaType,
  CollectionCard,
  CollectionCardRarity,
} from 'src/services/apollo/generated/graphql'
import { CollectionCardType } from 'src/services/apollo/generated/graphql'
import { cx } from 'src/utils/cx'
import { useHoverMediaQuery } from 'src/utils/hooks/useHoverMediaQuery'
import { getImagePublicUrl, getImageThumbnailUrl } from 'src/utils/media'
import { track } from 'src/utils/tracker'
import type {
  CollectionCardDataLiveSchema,
  CollectionCardDataMediaType,
  CollectionCardDataStaticSchema,
} from '../UserDecoration/schema/collectionCard'
import { getCollectionCardDataSchema } from '../UserDecoration/schema/collectionCard'

type CollectionCardLiveData = v.InferInput<typeof CollectionCardDataLiveSchema>
type CollectionCardStaticData = v.InferInput<
  typeof CollectionCardDataStaticSchema
>

type CollectionCardItemProps = {
  collectionCard: CollectionCard
  owned: boolean
  showOverlay?: boolean
  trackingArgs?: Record<string, JsonValue | undefined>
}

/**
 * Before 2025/09/19, we have only a media field which holds mediaId.
 * After 2025/09/19, we have a new mediaId field for this, and media field will always be media's URL for consistency.
 * This function is for backward compatibility.
 *
 * @param input
 * @param type
 * @returns
 */
export function getUrlForCollectionCardMedia(
  mediaData: CollectionCardDataMediaType,
  type?: 'thumbnail' | 'public',
): string | undefined {
  const mediaSource = mediaData.mediaId || mediaData.media

  if (mediaSource && !mediaSource.startsWith('http'))
    // mediaSource is a mediaId
    return type === 'thumbnail'
      ? getImageThumbnailUrl(mediaSource)
      : getImagePublicUrl(mediaSource)

  return mediaSource // it's a URL
}

export function CollectionCardItem(props: CollectionCardItemProps) {
  const getText = useLocalizedStringPairGetter()
  const { collectionCard } = props
  const type = collectionCard.type

  const parsedData = v.safeParse(
    getCollectionCardDataSchema(type),
    collectionCard.data,
  )

  if (!parsedData.success) {
    return (
      <article className="relative aspect-[3/5] bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
        <p className="italic opacity-50 items-center">
          <Text i18nKey="user:user-decoration.collection-card.invalid-card" />
        </p>
      </article>
    )
  }

  const cardName = getText(parsedData.output.name, 'en')
  const trackingArgs = {
    name: cardName,
    // merge trackingArgs with the one from cardSet
    ...props.trackingArgs,
  }

  const cardContent = match(type)
    .with(CollectionCardType.Live, () => {
      return (
        <CollectionCardLiveItem
          owned={props.owned}
          data={parsedData.output as CollectionCardLiveData}
          trackingArgs={trackingArgs}
        />
      )
    })
    .otherwise(() => {
      return (
        <CollectionCardStaticItem
          owned={props.owned}
          data={parsedData.output as CollectionCardStaticData}
          trackingArgs={trackingArgs}
        />
      )
    })

  return (
    // Aspect ratio is currently hardcoded
    <article className="relative aspect-[3/5] bg-gray-50 dark:bg-gray-800">
      {cardContent}
      {props.showOverlay && (
        <CollectionCardItemOverlay
          owned={props.owned}
          collectionCardData={parsedData.output}
          rarity={props.collectionCard.rarity}
          className="absolute bottom-0 w-full"
        />
      )}
    </article>
  )
}

function CollectionCardStaticItem(props: {
  data: CollectionCardStaticData
  owned: boolean
  trackingArgs?: Record<string, JsonValue | undefined>
}) {
  const coverIsImage = props.data.mediaType === 'IMAGE'
  const coverImg =
    coverIsImage && getUrlForCollectionCardMedia(props.data, 'public')
  const thumbnailImg = getUrlForCollectionCardMedia(props.data, 'thumbnail')
  const mediaSize = { width: 768, height: 1280 }
  return (
    <div
      className={cx(
        'relative',
        !props.owned &&
          "after:content-[''] after:absolute after:inset-0 after:bg-neutral-200/80 after:dark:bg-neutral-600/80 pointer-events-none",
      )}
    >
      {coverImg && (
        <Gallery>
          <Item
            original={coverImg}
            width={mediaSize.width}
            height={mediaSize.height}
          >
            {({ ref, open }) => (
              <ImageWithLoading
                className=""
                src={thumbnailImg}
                ref={ref}
                onClick={e => {
                  track('collection_card_media_click', {
                    type: CollectionCardType.Static,
                    mediaType: props.data.mediaType,
                    ...props.trackingArgs,
                  })
                  open(e)
                }}
              />
            )}
          </Item>
        </Gallery>
      )}
    </div>
  )
}

function CollectionCardLiveItem(props: {
  data: CollectionCardLiveData
  owned: boolean
  trackingArgs?: Record<string, JsonValue | undefined>
}) {
  const { mediaType, live } = props.data
  const mediaSize = { width: 768, height: 1280 }
  const coverIsImage = mediaType === 'IMAGE'
  const thumbnailUrl = coverIsImage
    ? getUrlForCollectionCardMedia(props.data, 'thumbnail')
    : undefined
  const canHover = useHoverMediaQuery()
  const videoPlayerRef = useRef<VideoPlayerWithFullscreenRef>(null!)

  const onMouseEnter = useCallback(() => {
    if (!canHover) return
    videoPlayerRef.current.playVideo()
  }, [canHover])

  const onMouseLeave = useCallback(() => {
    if (!canHover) return
    videoPlayerRef.current.stopVideo()
  }, [canHover])

  const onClick = useCallback(
    (e: MouseEvent) => {
      if (
        e.target instanceof HTMLVideoElement ||
        e.target instanceof HTMLImageElement
      ) {
        videoPlayerRef.current.playVideo({ fullscreen: true })
      }
    },
    [canHover],
  )

  return (
    <div
      className={cx(
        'relative',
        !props.owned &&
          "after:content-[''] after:absolute after:inset-0 after:bg-neutral-200/80 after:dark:bg-neutral-600/80 pointer-events-none",
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {thumbnailUrl && (
        <ImageWithLoading
          src={thumbnailUrl}
          height={mediaSize.height}
          width={mediaSize.width}
        />
      )}
      <VideoPlayerWithFullscreen
        ref={videoPlayerRef}
        className="absolute inset-0 aspect-[3/5] object-cover"
        mediaSpec={live.media}
        mediaType={live.mediaType}
        trackingArgs={props.trackingArgs}
      />
    </div>
  )
}

const overlayStyles = tv({
  slots: {
    wrapper:
      'px-2 py-4 min-h-[5em] backdrop-blur bg-neutral-100/70 dark:bg-neutral-800/70 content-center',
    text: 'text-sm text-center font-medium',
  },
  variants: {
    owned: {
      true: {},
      false: {
        text: 'text-black/20 dark:text-white/20',
      },
    },
    rarity: {
      N: {},
      SR: {
        text: 'text-violet-600 dark:text-violet-300',
      },
      SSR: {
        text: 'text-orange-700 dark:text-orange-300',
      },
    },
  },
})

function CollectionCardItemOverlay(
  props: {
    collectionCardData: CollectionCardStaticData
    rarity: CollectionCardRarity
    owned: boolean
  } & StyleProps,
) {
  const getText = useLocalizedStringPairGetter()
  return (
    <footer
      className={overlayStyles({
        owned: props.owned,
        rarity: props.rarity,
      }).wrapper({ className: props.className })}
    >
      <h3
        className={overlayStyles({
          owned: props.owned,
          rarity: props.rarity,
        }).text()}
      >
        {getText(props.collectionCardData.name)}
      </h3>
    </footer>
  )
}

export function CollectionCardCoverBackground(props: {
  collectionCard?: CollectionCard
}) {
  const collectionCard = props.collectionCard
  const parsedData = v.safeParse(
    getCollectionCardDataSchema(collectionCard?.type),
    collectionCard?.data,
  )

  if (!parsedData.success) return null

  const coverImg = getUrlForCollectionCardMedia(parsedData.output, 'thumbnail')
  return (
    <ImageWithLoading
      src={coverImg}
      wrapperProps={{ className: 'aspect-[3/5]' }}
    />
  )
}

type VideoPlayerWithFullscreenRef = {
  playVideo: (opts?: { fullscreen?: boolean }) => void
  stopVideo: () => void
}
const fullscreenClassname = 'max-w-full max-h-screen object-contain'
/**
 * Only video-hls and video can be fullscreened
 * TODO: Refactor
 *
 * @param props
 * @returns
 */
const VideoPlayerWithFullscreen = forwardRef<
  VideoPlayerWithFullscreenRef,
  {
    className?: string
    mediaType?: CampaignMediaType
    mediaSpec?: string
    trackingArgs?: Record<string, JsonValue | undefined>
  }
>(function VideoPlayerWithFullscreen(props, ref) {
  const [full, setFull] = useState(false)
  const [showVideo, setShowVideo] = useState(false)
  const [mediaLoading, setMediaLoading] = useState(false)
  const onMediaLoad = useCallback((state: boolean) => {
    setMediaLoading(state)
  }, [])
  const showPreloader = mediaLoading

  useImperativeHandle(ref, () => ({
    playVideo: opts => {
      setShowVideo(true)
      if (opts?.fullscreen) {
        track('collection_card_media_click', {
          type: CollectionCardType.Live,
          mediaType: props.mediaType,
          ...props.trackingArgs,
        })
        setFull(true)
      }
    },
    stopVideo: () => {
      setShowVideo(false)
      setFull(false)
      // Hide Preloader on stop
      onMediaLoad(false)
    },
  }))

  const onCanPlay = useCallback(() => {
    track('collection_card_live_play', {
      fullscreen: full,
      ...props.trackingArgs,
    })
    onMediaLoad(false)
  }, [full])

  const mediaContent = (() => {
    if (!showVideo) return null
    if (!props.mediaSpec) {
      return null
    }
    if (props.mediaType === 'IMAGE') {
      return <img className={cx(props.className)} src={props.mediaSpec} />
    } else {
      return (
        <MediaStreamPlayer
          mediaType={props.mediaType}
          mediaSpec={props.mediaSpec}
          className={full ? fullscreenClassname : props.className}
          commonProps={{
            onCanPlay: onCanPlay,
            onLoadStart: () => onMediaLoad(true),
          }}
        />
      )
    }
  })()

  return (
    <>
      {!full ? (
        <>{mediaContent}</>
      ) : (
        <FullWindowVideoForCollectionCard
          onClose={() => {
            setMediaLoading(false)
            setShowVideo(false)
            setFull(false)
          }}
          videoContent={mediaContent}
          showPreloader={showPreloader}
        ></FullWindowVideoForCollectionCard>
      )}
      <AnimatePresence>
        {showPreloader ? (
          <motion.div
            className="absolute inset-0 bg-white/40 flex justify-center items-center"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: {
                delay: 0.2,
                duration: 0.5,
                ease: 'easeOut',
              },
            }}
            exit={{
              opacity: 0,
              transition: {
                duration: 0.5,
                ease: 'easeIn',
              },
            }}
          >
            <Preloader />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  )
})

/**
 * Modified from existing FullWindowVideo
 * Not fullscreen but use portal to simulate a lightbox effect
 *
 * @param props
 * @returns
 */
function FullWindowVideoForCollectionCard(props: {
  onClose: () => void

  videoContent?: ReactNode
  showPreloader?: boolean
}) {
  return createPortal(
    <div
      className="left-0 top-0 fixed w-full h-full z-[100000] grid place-content-center bg-black/80 isolate"
      tabIndex={-1}
      role="dialog"
      onClick={() => props.onClose()}
    >
      {props.videoContent}
      <AnimatePresence>
        {props.showPreloader ? (
          <motion.div
            className="absolute inset-0 flex justify-center items-center"
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
              transition: {
                delay: 0.2,
                duration: 0.5,
                ease: 'easeOut',
              },
            }}
            exit={{
              opacity: 0,
              transition: {
                duration: 0.5,
                ease: 'easeIn',
              },
            }}
          >
            <Preloader dark />
          </motion.div>
        ) : null}
      </AnimatePresence>
      <div
        className="p-3 absolute right-0 flex items-center gap-4 text-white"
        // Respect the mobile webview safe area so the close button stays
        // tappable below the status bar notch. 0 on web.
        style={{
          top: 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
        }}
      >
        <button onClick={() => props.onClose()}>
          <IcBaselineClose
            aria-hidden="true"
            className="size-8"
          ></IcBaselineClose>
        </button>
      </div>
    </div>,
    document.body,
  )
}

/**
 * Simple wrapper with loading state
 * To avoid displaying partially loaded image
 */
const ImageWithLoading = forwardRef<
  HTMLImageElement,
  DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> & {
    wrapperProps?: DetailedHTMLProps<
      HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    >
  }
>(function ImageWithLoading({ className, wrapperProps, ...props }, ref) {
  const [loadingDisplayImg, setLoadingDisplayImg] = useState(true)
  return (
    <div
      {...wrapperProps}
      className={cx('relative isolate bg-skeleton', wrapperProps?.className)}
    >
      <img
        {...props}
        className={cx(loadingDisplayImg && 'opacity-0', 'aspect-[3/5]')}
        onLoadStart={() => setLoadingDisplayImg(true)}
        onLoad={() => setLoadingDisplayImg(false)}
        ref={ref}
      />
    </div>
  )
})
